import { useState, useCallback } from 'react';
import { PAGINATION_DEFAULTS } from '../types/pagination';

export interface UsePaginationOptions {
  /** Initial page number (default: 1) */
  initialPage?: number;
  /** Initial page size (default: 50) */
  initialLimit?: number;
}

export interface UsePaginationResult {
  /** Current page number (1-indexed) */
  page: number;
  /** Current page size */
  limit: number;
  /** Set the current page */
  setPage: (page: number) => void;
  /** Set the page size (also resets to page 1) */
  setLimit: (limit: number) => void;
  /** Reset to first page (useful when filters change) */
  resetToFirstPage: () => void;
}

/**
 * Hook to manage pagination state.
 * Provides page and limit state along with helper functions.
 */
export function usePagination(options?: UsePaginationOptions): UsePaginationResult {
  const [page, setPageState] = useState(options?.initialPage ?? PAGINATION_DEFAULTS.page);
  const [limit, setLimitState] = useState(options?.initialLimit ?? PAGINATION_DEFAULTS.limit);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setLimit = useCallback((newLimit: number) => {
    setLimitState(newLimit);
    // Reset to page 1 when limit changes
    setPageState(PAGINATION_DEFAULTS.page);
  }, []);

  const resetToFirstPage = useCallback(() => {
    setPageState(PAGINATION_DEFAULTS.page);
  }, []);

  return {
    page,
    limit,
    setPage,
    setLimit,
    resetToFirstPage,
  };
}
